import React, { useState } from "react";
import { useGlobalContext } from "../../context/GlobalContext";
import { Button, Dialog, IconButton } from "@mui/material";
import { Tabs } from "antd";
import CloseRounded from "@mui/icons-material/CloseRounded";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import styled from "styled-components";
import { vehicleLedgerTabs } from "../handlers/useVehicleLedgerHandler";
import VehicleLedgerLayoutHandler from "../../components/layouts/VehicleLedgerLayoutHandler";

const VehicleLedgerModal: React.FC = () => {
  const { data: globalData, updateData } = useGlobalContext();
  const { openVehicleLedger } = globalData || {};
  const [activeTab, setActiveTab] = useState("search_vehicle");
  const [modalKey, setModalKey] = useState(0);

  const handleClose = (): void => {
    // Reset active tab to first tab
    setActiveTab("search_vehicle");
    // Increment key to force remount of all tab content (resets forms and tables)
    setModalKey((prev) => prev + 1);
    // Close modal
    updateData("openVehicleLedger", false);
  };

  return (
    <Dialog
      open={!!openVehicleLedger}
      onClose={handleClose}
      aria-labelledby="vehicle-ledger-title"
      maxWidth="lg"
      keepMounted
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "var(--lp-r-lg)",
          boxShadow: "var(--lp-shadow-lg)",
          overflow: "hidden",
        },
      }}
    >
      <Header>
        <span className="icon-tile">
          <MenuBookOutlined fontSize="small" />
        </span>
        <div className="heading">
          <h2 id="vehicle-ledger-title">Vehicle Ledger</h2>
          <p>
            Receipts, invoices, defaults, advances and stickers for a vehicle.
          </p>
        </div>
        <IconButton aria-label="Close" size="small" onClick={handleClose}>
          <CloseRounded fontSize="small" />
        </IconButton>
      </Header>

      <LedgerTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={vehicleLedgerTabs.map((tab) => ({
          label: tab.label,
          key: tab.key,
          children: (
            <VehicleLedgerLayoutHandler
              key={`${tab.key}-${modalKey}`}
              tabKey={tab.key}
              vehiclePlate={openVehicleLedger?.vehiclePlate}
            />
          ),
        }))}
      />

      <Footer>
        <Button
          onClick={handleClose}
          sx={{ textTransform: "none", color: "var(--lp-muted)" }}
        >
          Close
        </Button>
      </Footer>
    </Dialog>
  );
};

export default VehicleLedgerModal;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px;

  .icon-tile {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: var(--lp-r-sm);
    background: var(--lp-primary-soft);
    color: var(--lp-primary);
  }

  .heading {
    flex: 1;
    min-width: 0;
  }

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--lp-text);
    letter-spacing: -0.01em;
  }

  p {
    margin: 2px 0 0;
    font-size: 13px;
    color: var(--lp-muted);
  }
`;

const LedgerTabs = styled(Tabs)`
  flex: 1;
  min-height: 0;

  && > .ant-tabs-nav {
    margin: 0;
    padding: 0 20px;
    border-bottom: 1px solid var(--lp-border);

    &::before {
      display: none;
    }
  }

  .ant-tabs-tab {
    padding: 10px 2px;
    font-size: 13px;
    font-weight: 500;
    color: var(--lp-muted);

    &:hover {
      color: var(--lp-primary);
    }
  }

  .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 22px;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--lp-primary);
    font-weight: 600;
  }

  .ant-tabs-ink-bar {
    height: 2px;
    border-radius: var(--lp-r-pill);
    background: var(--lp-primary);
  }

  /* the body scrolls under a fixed header, tab bar and footer */
  .ant-tabs-content-holder {
    max-height: 68vh;
    overflow-y: auto;
    padding: 20px;
    background: var(--lp-bg-alt);
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 10px 20px;
  border-top: 1px solid var(--lp-border);
`;
